import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TrackingItemDto } from 'api/deviceChecklist/models/TrackingItemDto';
import { Paged } from 'api/common/types/Page';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';

const BoolCell: React.FC<{ value: boolean | null | undefined; color?: string }> = ({ value, color }) => {
    if (value === null || value === undefined) return <span className="text-muted">-</span>;
    return value
        ? <FontAwesomeIcon icon={faCheck} className={color || 'text-success'} />
        : <FontAwesomeIcon icon={faTimes} className="text-muted" />;
};

const ColHeader: React.FC<{ label: string; dbCol: string }> = ({ label, dbCol }) => (
    <th style={{ whiteSpace: 'nowrap' }}>
        {label}
        <br />
        <small className="text-muted fw-normal">({dbCol})</small>
    </th>
);

interface TrackingItemsTableProps {
    trackingPage: Paged<TrackingItemDto>;
    currentPage: number;
    onPageChange: (page: number) => void;
    className?: string;
    style?: React.CSSProperties;
}

const TrackingItemsTable: React.FC<TrackingItemsTableProps> = ({ trackingPage, currentPage, onPageChange, className, style }) => {
    const { t } = useTranslation();

    return (
        <>
            <Table striped hover responsive className={`mb-0 ${className ?? ''}`} style={style}>
                <thead>
                    <tr>
                        <ColHeader label="ID" dbCol="id" />
                        <ColHeader label={t('tracking_items.date')} dbCol="date" />
                        <ColHeader label={t('tracking_items.device_code')} dbCol="device_code" />
                        <ColHeader label={t('tracking_items.latitude')} dbCol="latitude" />
                        <ColHeader label={t('tracking_items.longitude')} dbCol="longitude" />
                        <ColHeader label={t('tracking_items.altitude')} dbCol="altitude" />
                        <ColHeader label={t('tracking_items.angle')} dbCol="angle" />
                        <ColHeader label={t('tracking_items.speed')} dbCol="speed" />
                        <ColHeader label={t('tracking_items.satellites')} dbCol="satellites" />
                        <ColHeader label={t('tracking_items.km')} dbCol="km" />
                        <ColHeader label={t('tracking_items.status')} dbCol="status" />
                        <ColHeader label={t('tracking_items.real_km')} dbCol="real_km" />
                        <ColHeader label={t('tracking_items.fuel')} dbCol="fuel" />
                        <ColHeader label={t('tracking_items.fuel_level')} dbCol="fuel_level" />
                        <ColHeader label={t('tracking_items.driver_tag')} dbCol="driver_tag" />
                        <ColHeader label={t('tracking_items.ignition')} dbCol="ignition" />
                        <ColHeader label={t('tracking_items.movement')} dbCol="movement" />
                        <ColHeader label={t('tracking_items.idling')} dbCol="idling" />
                        <ColHeader label={t('tracking_items.is_tag')} dbCol="is_tag" />
                        <ColHeader label={t('tracking_items.on_trip')} dbCol="on_trip" />
                        <ColHeader label={t('tracking_items.unplug_detection')} dbCol="unplug_detection" />
                        <ColHeader label={t('tracking_items.towing_detection')} dbCol="towing_detection" />
                        <ColHeader label={t('tracking_items.penalty')} dbCol="penalty" />
                        <ColHeader label={t('tracking_items.harsh_braking_events')} dbCol="harsh_braking_events" />
                        <ColHeader label={t('tracking_items.harsh_cornering_events')} dbCol="harsh_cornering_events" />
                        <ColHeader label={t('tracking_items.harsh_acceleration_events')} dbCol="harsh_acceleration_events" />
                        <ColHeader label={t('tracking_items.alarm')} dbCol="alarm" />
                    </tr>
                </thead>
                <tbody>
                    {trackingPage.items.map(item => (
                        <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>{new Date(item.date).toLocaleString()}</td>
                            <td>{item.deviceCode || '-'}</td>
                            <td>{item.latitude}</td>
                            <td>{item.longitude}</td>
                            <td>{item.altitude}</td>
                            <td>{item.angle}°</td>
                            <td>{item.speed} km/h</td>
                            <td>{item.satellites}</td>
                            <td>{item.km}</td>
                            <td>{item.status || '-'}</td>
                            <td><BoolCell value={item.realKm} /></td>
                            <td>{item.fuel}</td>
                            <td>{item.fuelLevel}</td>
                            <td>{item.driverTag || '-'}</td>
                            <td><BoolCell value={item.ignition} /></td>
                            <td><BoolCell value={item.movement} /></td>
                            <td><BoolCell value={item.idling} color="text-warning" /></td>
                            <td><BoolCell value={item.isTag} /></td>
                            <td><BoolCell value={item.onTrip} /></td>
                            <td><BoolCell value={item.unplugDetection} color="text-danger" /></td>
                            <td><BoolCell value={item.towingDetection} color="text-danger" /></td>
                            <td>{item.penalty}</td>
                            <td>{item.harshBrakingEvents}</td>
                            <td>{item.harshCorneringEvents}</td>
                            <td>{item.harshAccelerationEvents}</td>
                            <td>{item.alarm || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <div className="p-3">
                <PaginationWithInfo
                    currentPage={currentPage}
                    totalItems={trackingPage.totalItems}
                    itemsPerPage={DEFAULT_PAGINATION_ITEMS_PER_PAGE}
                    pageItems={trackingPage.items.length}
                    itemName={t('tracking_items.title')}
                    onChange={onPageChange}
                />
            </div>
        </>
    );
};

export default TrackingItemsTable;
